import React, { useState } from 'react';
import { getAvailableDashboards } from '../../utils/roleRouting';
import './RolePicker.css';

/**
 * Presents the dashboards available to the user based on their backend-returned roles.
 * Shown after successful authentication when the user has more than one role.
 *
 * @param {string[]} roles - API role strings returned by the backend
 * @param {Function} onSelect - Called with the chosen ROLE_DASHBOARDS entry
 * @param {string} [userName] - Optional user name for a personalized greeting
 */
const RolePicker = ({ roles, onSelect, userName }) => {
  const dashboards = getAvailableDashboards(roles);
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState('');

  const handleSelect = async (dash) => {
    if (selecting) return;
    setSelecting(dash.apiRole);
    setError('');
    try {
      await onSelect(dash);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="role-picker-overlay">
      <div className="role-picker-card">
        <div className="role-picker-header">
          <h2 className="role-picker-title">Welcome back{userName ? `, ${userName}` : ''}!</h2>
          <p className="role-picker-subtitle">Choose how you'd like to continue.</p>
        </div>

        {error && (
          <div className="role-picker-error" role="alert">{error}</div>
        )}

        <div className="role-picker-grid">
          {dashboards.map((dash) => (
            <button
              key={`${dash.apiRole}-${dash.route}`}
              type="button"
              className="role-picker-option"
              onClick={() => handleSelect(dash)}
              disabled={!!selecting}
            >
              <span className="role-picker-icon">{dash.icon}</span>
              <span className="role-picker-label">{dash.label}</span>
              <span className="role-picker-desc">{dash.description}</span>
              {selecting === dash.apiRole && (
                <span className="role-picker-loading">Loading…</span>
              )}
            </button>
          ))}
        </div>

        {dashboards.length === 0 && (
          <div className="role-picker-empty">
            No dashboards are assigned to your account. Please contact support.
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePicker;
